/**
 * /plyx/settings/webhooks — creator self-serve management of their
 * external community integrations. Each subscription is a POST endpoint
 * (typically a Discord/Telegram/Slack incoming webhook URL) that Plyx
 * will fire whenever a chosen event happens on the creator's account
 * (going live, tips landing during a Mini Live Hour, etc.).
 *
 * Backend lives in /app/backend/routes/plyx_webhooks.py — three
 * endpoints: list, create, delete. Each subscription rendered here
 * also surfaces the last delivery result + lifetime success/failure
 * counts so creators can diagnose silent failures themselves instead
 * of pinging support.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { plyxApi } from "@/lib/plyxApi";
import { usePlyxAuth } from "@/context/PlyxAuthContext";
import { toast } from "sonner";
import {
  Loader2, Plus, Trash2, Radio, Zap, Gift, ChevronLeft,
  CheckCircle2, AlertTriangle, Webhook, Send,
} from "lucide-react";

const EVENTS = [
  {
    key: "creator.first_live_today",
    icon: Radio,
    label: "I go live today (first time)",
    blurb: "Fires once per UTC day, the first time you start a stream. Perfect for a Discord #live channel ping.",
  },
  {
    key: "mini_hour.start",
    icon: Zap,
    label: "I trigger a Mini Live Hour boost",
    blurb: "Fires when you tap the 5-minute boost button mid-stream. Use it to alert your hardest-core community.",
  },
  {
    key: "live.tip",
    icon: Gift,
    label: "I receive a tip during a stream",
    blurb: "Coalesced — quiet windows get one ping; tip-storms get one summary ping every PLYX_TIP_NOTIF_COALESCE_SEC.",
  },
];

const presetExample = (key) => {
  switch (key) {
    case "discord":
      return "https://discord.com/api/webhooks/123…/abc…";
    case "telegram":
      // Telegram bot webhook proxies typically look like this.
      return "https://api.telegram.org/bot<TOKEN>/sendMessage?chat_id=…";
    case "slack":
      return "https://hooks.slack.com/services/T…/B…/…";
    default:
      return "https://example.com/webhook";
  }
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

const PlyxWebhookSettings = () => {
  const { user, loading: authLoading } = usePlyxAuth();
  const [webhooks, setWebhooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [form, setForm] = useState({
    preset: "discord",
    url: "",
    secret: "",
    events: ["creator.first_live_today"],
  });

  const load = async () => {
    setLoading(true);
    try {
      const r = await plyxApi.get("/plyx/me/webhooks");
      setWebhooks(r.data.webhooks || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load webhooks");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authLoading) return;
    if (!user) return;
    load();
  }, [user, authLoading]);

  const toggleEvent = (key) => {
    setForm((f) => {
      const next = new Set(f.events);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return { ...f, events: Array.from(next) };
    });
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (creating) return;
    const url = form.url.trim();
    if (!/^https?:\/\//i.test(url)) {
      toast.error("URL must start with http:// or https://");
      return;
    }
    if (form.events.length === 0) {
      toast.error("Pick at least one event to subscribe to.");
      return;
    }
    setCreating(true);
    try {
      await plyxApi.post("/plyx/me/webhooks", {
        url,
        events: form.events,
        secret: form.secret.trim() || undefined,
      });
      toast.success("Webhook subscription added");
      setForm({ preset: form.preset, url: "", secret: "", events: ["creator.first_live_today"] });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create webhook");
    } finally {
      setCreating(false);
    }
  };

  const remove = async (id) => {
    if (deletingId) return;
    if (!window.confirm("Stop sending events to this URL?")) return;
    setDeletingId(id);
    try {
      await plyxApi.delete(`/plyx/me/webhooks/${id}`);
      setWebhooks((w) => w.filter((x) => x.webhook_id !== id));
      toast.success("Subscription removed");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    } finally {
      setDeletingId(null);
    }
  };

  // ── Send a synthetic "test.ping" so creators can verify their
  // Discord/Slack/Telegram channel actually receives the payload —
  // no need to wait for a real Mini Hour boost to fire. The backend
  // updates last_delivery + counters exactly like a real event, so
  // success/failure is immediately reflected in the row stats.
  const [testingId, setTestingId] = useState(null);
  const sendTest = async (id) => {
    if (testingId) return;
    setTestingId(id);
    try {
      const r = await plyxApi.post(`/plyx/me/webhooks/${id}/test`);
      if (r.data?.ok) {
        toast.success("Test ping delivered ✓ — check your channel");
      } else {
        const err = r.data?.delivery?.error || `HTTP ${r.data?.delivery?.status || "?"}`;
        toast.error(`Test failed: ${err}`);
      }
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Test failed");
    } finally {
      setTestingId(null);
    }
  };

  if (authLoading || (!user && !loading)) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <div className="max-w-3xl mx-auto px-4 py-6">
        <Link
          to="/plyx/settings"
          data-testid="plyx-webhooks-back"
          className="inline-flex items-center gap-1 text-white/60 hover:text-white text-sm mb-4"
        >
          <ChevronLeft className="w-4 h-4" /> Back to settings
        </Link>

        <div className="flex items-center gap-3 mb-2">
          <div
            className="w-10 h-10 rounded-2xl flex items-center justify-center text-[#0A0A0A]"
            style={{ background: "linear-gradient(135deg, #FF2D7A, #FFB42D, #00E0C7)" }}
          >
            <Webhook className="w-5 h-5" />
          </div>
          <h1 className="font-display text-3xl">Webhooks</h1>
        </div>
        <p className="text-white/60 text-sm mb-8 max-w-xl">
          Wire your Discord, Telegram, or Slack channel into Plyx events.
          We&apos;ll POST a JSON body to your URL whenever the chosen event
          fires for your account. No code on your side — just paste your
          channel&apos;s incoming-webhook URL.
        </p>

        {/* Existing subscriptions */}
        <section data-testid="plyx-webhooks-list" className="mb-10">
          <h2 className="font-bold text-lg mb-3">Your subscriptions</h2>
          {loading ? (
            <div className="text-white/50 text-sm py-4 flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          ) : webhooks.length === 0 ? (
            <div className="text-white/50 text-sm py-6 px-4 rounded-xl border border-dashed border-white/15">
              You haven&apos;t set up any webhooks yet. Add one below to get started.
            </div>
          ) : (
            <div className="space-y-3">
              {webhooks.map((w) => {
                const last = w.last_delivery || {};
                const ok = w.delivery_count > 0 && (w.failure_count === 0 || (last && last.ok));
                return (
                  <div
                    key={w.webhook_id}
                    data-testid={`plyx-webhook-row-${w.webhook_id}`}
                    className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 flex items-start gap-3"
                  >
                    <div className="mt-0.5">
                      {ok
                        ? <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                        : <AlertTriangle className="w-5 h-5 text-[#FFB42D]" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <code className="block text-sm font-mono text-white/85 truncate">{w.url}</code>
                      <div className="mt-1 flex flex-wrap gap-1">
                        {w.events.map((e) => (
                          <span key={e} className="text-[10px] px-1.5 py-0.5 rounded bg-white/10 text-white/70">{e}</span>
                        ))}
                        {w.has_secret && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300">signed</span>
                        )}
                      </div>
                      <div className="mt-2 text-[11px] text-white/50 flex flex-wrap gap-x-3 gap-y-1">
                        <span>Deliveries: <strong className="text-white">{w.delivery_count}</strong></span>
                        <span>Failures: <strong className={w.failure_count > 0 ? "text-[#FF2D7A]" : "text-white"}>{w.failure_count}</strong></span>
                        <span>Last: {last.at ? `${fmtDate(last.at)} · HTTP ${last.status ?? "?"}` : "never"}</span>
                      </div>
                      {last && last.error && (
                        <div className="mt-1 text-[11px] text-[#FF2D7A] truncate">⚠ {last.error}</div>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => sendTest(w.webhook_id)}
                        disabled={testingId === w.webhook_id}
                        data-testid={`plyx-webhook-test-${w.webhook_id}`}
                        className="inline-flex items-center justify-center w-9 h-9 rounded-full hover:bg-emerald-500/10 hover:text-emerald-300 text-white/50 transition-colors disabled:opacity-60"
                        title="Send test ping"
                        aria-label="Send test ping"
                      >
                        {testingId === w.webhook_id
                          ? <Loader2 className="w-4 h-4 animate-spin" />
                          : <Send className="w-4 h-4" />}
                      </button>
                      <button
                        type="button"
                        onClick={() => remove(w.webhook_id)}
                        disabled={deletingId === w.webhook_id}
                        data-testid={`plyx-webhook-delete-${w.webhook_id}`}
                        className="inline-flex items-center justify-center w-9 h-9 rounded-full hover:bg-gradient-to-r from-[#FF2D7A] via-[#FFB42D] to-[#00E0C7]/10 hover:text-[#FF2D7A] text-white/50 transition-colors disabled:opacity-60"
                        title="Delete subscription"
                        aria-label="Delete subscription"
                      >
                        {deletingId === w.webhook_id
                          ? <Loader2 className="w-4 h-4 animate-spin" />
                          : <Trash2 className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* Create form */}
        <section data-testid="plyx-webhooks-new">
          <h2 className="font-bold text-lg mb-3">Add a webhook</h2>
          <form onSubmit={submit} className="space-y-4 rounded-2xl border border-white/10 bg-white/[0.02] p-4">
            <div>
              <label className="text-xs uppercase tracking-wide text-white/50 mb-1 block">Preset</label>
              <div className="flex gap-2 flex-wrap">
                {["discord", "telegram", "slack", "custom"].map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setForm({ ...form, preset: p })}
                    data-testid={`plyx-webhook-preset-${p}`}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${
                      form.preset === p
                        ? "border-[#FF2D7A] bg-gradient-to-r from-[#FF2D7A] via-[#FFB42D] to-[#00E0C7]/15 text-white"
                        : "border-white/15 text-white/70 hover:bg-white/5"
                    }`}
                  >
                    {p[0].toUpperCase() + p.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label htmlFor="wh-url" className="text-xs uppercase tracking-wide text-white/50 mb-1 block">
                Endpoint URL
              </label>
              <input
                id="wh-url"
                type="url"
                required
                value={form.url}
                onChange={(e) => setForm({ ...form, url: e.target.value })}
                placeholder={presetExample(form.preset)}
                data-testid="plyx-webhook-url"
                className="w-full px-3 py-2 rounded-xl bg-black border border-white/15 text-sm font-mono text-white placeholder-white/30 focus:outline-none focus:border-[#FF2D7A]"
              />
            </div>

            <div>
              <label htmlFor="wh-secret" className="text-xs uppercase tracking-wide text-white/50 mb-1 block">
                Secret (optional — signs the payload with HMAC-SHA256)
              </label>
              <input
                id="wh-secret"
                type="password"
                value={form.secret}
                onChange={(e) => setForm({ ...form, secret: e.target.value })}
                placeholder="leave blank to skip signing"
                data-testid="plyx-webhook-secret"
                className="w-full px-3 py-2 rounded-xl bg-black border border-white/15 text-sm font-mono text-white placeholder-white/30 focus:outline-none focus:border-[#FF2D7A]"
              />
            </div>

            <div>
              <label className="text-xs uppercase tracking-wide text-white/50 mb-2 block">
                Events to subscribe to
              </label>
              <div className="space-y-2">
                {EVENTS.map(({ key, label, blurb, icon: Icon }) => {
                  const checked = form.events.includes(key);
                  return (
                    <label
                      key={key}
                      htmlFor={`wh-event-${key}`}
                      className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                        checked
                          ? "border-[#FF2D7A]/50 bg-gradient-to-r from-[#FF2D7A] via-[#FFB42D] to-[#00E0C7]/5"
                          : "border-white/10 hover:bg-white/5"
                      }`}
                    >
                      <input
                        id={`wh-event-${key}`}
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleEvent(key)}
                        data-testid={`plyx-webhook-event-${key}`}
                        className="mt-1 w-4 h-4 accent-[#FF2D7A]"
                      />
                      <Icon className="w-4 h-4 mt-1 text-[#FFB42D]" />
                      <div className="text-sm">
                        <div className="font-semibold text-white">{label}</div>
                        <p className="text-white/55 text-xs mt-0.5 leading-snug">{blurb}</p>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>

            <button
              type="submit"
              disabled={creating}
              data-testid="plyx-webhook-create"
              className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full font-semibold text-white disabled:opacity-50"
              style={{ background: "linear-gradient(135deg, #FF2D7A, #FFB42D, #00E0C7)" }}
            >
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              {creating ? "Saving…" : "Add webhook"}
            </button>
          </form>
        </section>
      </div>
    </div>
  );
};

export default PlyxWebhookSettings;
